/**
 * BootErrorBoundary — top-level error boundary that wraps the entire app.
 * If ANY render error occurs (including during startup), shows the CrashScreen
 * instead of a blank black/white screen.
 */
import { Component, ReactNode } from "react";
import { CrashScreen } from "./CrashScreen";
import { setBootError } from "@/lib/bootGuard";
import { captureError } from "@/lib/observability";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class BootErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[BootErrorBoundary] Fatal render error:", error.message);
    console.error("[BootErrorBoundary] Component stack:", info.componentStack);
    setBootError(error);
    captureError("react.boot_boundary", error, {
      severity: "fatal",
      metadata: { componentStack: info.componentStack?.slice(0, 500) },
    });
  }

  render() {
    if (this.state.error) {
      return <CrashScreen error={this.state.error} context="React render error" />;
    }
    return this.props.children;
  }
}
